<script setup lang="ts">
import { storeToRefs } from 'pinia';
import {
    mdiCheckCircle,
    mdiAlertOutline,
    mdiAlertCircle,
    mdiInformationOutline,
    mdiClose
} from '@mdi/js';
import { useToastStore, type ToastKind } from '@/stores/toast';
import MdiIcon from './MdiIcon.vue';

const store = useToastStore();
const { items } = storeToRefs(store);

const ICONS: Record<ToastKind, string> = {
    info: mdiInformationOutline,
    success: mdiCheckCircle,
    warn: mdiAlertOutline,
    error: mdiAlertCircle
};
</script>

<template>
    <div class="toast-stack" role="status" aria-live="polite">
        <TransitionGroup name="toast">
            <div
                v-for="t in items"
                :key="t.id"
                class="toast"
                :class="`toast--${t.kind}`"
            >
                <MdiIcon :path="ICONS[t.kind as ToastKind]" :size="20" />
                <div class="toast__body">
                    <p class="toast__title">{{ t.title }}</p>
                    <p v-if="t.message" class="toast__msg">{{ t.message }}</p>
                </div>
                <button class="toast__close" @click="store.dismiss(t.id)" type="button" aria-label="Закрыть">
                    <MdiIcon :path="mdiClose" :size="16" />
                </button>
            </div>
        </TransitionGroup>
    </div>
</template>

<style scoped>
.toast-stack {
    position: fixed;
    top: 1rem;
    right: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    z-index: 100;
    max-width: 400px;
    pointer-events: none;
}
.toast {
    pointer-events: auto;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.7rem 0.85rem;
    background: var(--nav-pane-bg);
    border: 1px solid var(--shell-divider);
    border-radius: 8px;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
}
.toast--info {
    border-left: 3px solid var(--state-info);
    color: var(--state-info);
}
.toast--success {
    border-left: 3px solid var(--state-ok);
    color: var(--state-ok);
}
.toast--warn {
    border-left: 3px solid var(--state-warn);
    color: var(--state-warn);
}
.toast--error {
    border-left: 3px solid var(--state-err);
    color: var(--state-err);
}
.toast__body {
    color: var(--ink);
}
.toast__title {
    margin: 0;
    font-weight: 600;
    font-size: 0.9rem;
}
.toast__msg {
    margin: 0.15rem 0 0;
    font-size: 0.85rem;
    color: var(--ink-soft);
}
.toast__close {
    border: 0;
    background: transparent;
    padding: 0.15rem;
    border-radius: 4px;
    color: var(--ink-mute);
    cursor: pointer;
}
.toast__close:hover {
    background: var(--hover-soft);
    color: var(--ink);
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}
.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateX(20px);
}
</style>